import React, { useState, useEffect, useRef } from 'react';
import { uploadCharacterImage } from '../../services/storyToolsService';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Grid,
  Paper,
  Tabs,
  Tab,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  ImageList,
  ImageListItem,
  InputAdornment,
  CircularProgress,
  useTheme
} from '@mui/material';
import { 
  Close as CloseIcon,
  Person as PersonIcon,
  Face as FaceIcon,
  Psychology as PsychologyIcon,
  History as HistoryIcon,
  Collections as CollectionsIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Upload as UploadIcon,
  Save as SaveIcon,
  AutoAwesome as AutoAwesomeIcon,
  AutoFixHigh as AutoFixHighIcon,
  Link as LinkIcon,
  ZoomIn as ZoomInIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';

// Character role options with descriptions and groupings
const characterRoles = [
  { value: 'protagonist', label: 'Protagonist', description: 'The main character whose journey the story follows. They drive the plot forward and typically undergo significant change.', group: 'Main roles' },
  { value: 'deuteragonist', label: 'Deuteragonist', description: 'The second most important character who often has their own arc and closely intersects with the protagonist.', group: 'Main roles' },
  { value: 'tritagonist', label: 'Tritagonist', description: 'A third key character who adds dimension to the main conflict or supports a secondary storyline.', group: 'Main roles' },
  { value: 'antagonist', label: 'Antagonist', description: 'Character or force that opposes the protagonist, creating the central conflict.', group: 'Main roles' },
  { value: 'antihero', label: 'Antihero', description: 'Central character who lacks traditional heroic qualities but is still the focus of the story.', group: 'Main roles' },
  { value: 'mentor', label: 'Mentor', description: 'Guides the protagonist, offering wisdom, training, or support.', group: 'Supporting roles' },
  { value: 'ally', label: 'Ally', description: 'Supports the protagonist in their journey, often providing assistance or companionship.', group: 'Supporting roles' },
  { value: 'sidekick', label: 'Sidekick', description: 'A close companion to the protagonist who provides support and often comic relief.', group: 'Supporting roles' },
  { value: 'love-interest', label: 'Love Interest', description: 'A character who is romantically involved with or pursued by another character, often the protagonist.', group: 'Supporting roles' },
  { value: 'love-rival', label: 'Love Rival', description: 'Competes with another character for romantic attention, adding tension to relationships.', group: 'Supporting roles' },
  { value: 'foil', label: 'Foil', description: 'Contrasts with another character (usually the protagonist) to highlight particular qualities.', group: 'Supporting roles' },
  { value: 'catalyst', label: 'Catalyst', description: 'Instigates change or action in other characters but may not change themselves.', group: 'Supporting roles' },
  { value: 'comic-relief', label: 'Comic Relief', description: 'Provides humor to balance tension or heavy drama in the story.', group: 'Supporting roles' },
  { value: 'confidant', label: 'Confidant', description: 'A trusted character with whom another shares secrets, fears, and plans.', group: 'Supporting roles' },
  { value: 'rival', label: 'Rival', description: 'Competes with another character for status, power, or recognition, not always as a full antagonist.', group: 'Supporting roles' },
  { value: 'guardian', label: 'Guardian/Protector', description: 'Responsible for protecting another character, group, or place.', group: 'Supporting roles' },
  { value: 'authority-figure', label: 'Authority Figure', description: 'Represents institutional power (parent, boss, principal, commander) that can support or obstruct the protagonist.', group: 'Supporting roles' },
  { value: 'narrator', label: 'Narrator', description: 'Tells the story, either as a character within it or as an outside observer.', group: 'Supporting roles' }
];

// Character archetype options with descriptions
const characterArchetypes = [
  { value: 'hero', label: 'Hero', description: 'Embodies courage and nobility, willing to sacrifice for the greater good.' },
  { value: 'everyman', label: 'Everyman', description: 'An ordinary person placed in extraordinary circumstances, representing the audience point of view.' },
  { value: 'innocent', label: 'Innocent', description: 'Pure-hearted, optimistic, and often naive; seeks happiness and safety.' },
  { value: 'explorer', label: 'Explorer', description: 'Driven by curiosity and a desire for freedom, constantly seeking new experiences.' },
  { value: 'rebel', label: 'Rebel', description: 'Challenges authority, rules, or the status quo, often to bring about change.' },
  { value: 'ruler', label: 'Ruler', description: 'Seeks control, order, and stability; often leads a group, kingdom, or organization.' },
  { value: 'creator', label: 'Creator', description: 'Imaginative and driven to build or design something meaningful that outlasts them.' },
  { value: 'caregiver', label: 'Caregiver', description: 'Compassionate and selfless, focused on protecting and caring for others.' },
  { value: 'shadow', label: 'Shadow', description: 'Represents the darker aspects of humanity, often embodying what the hero fears or rejects.' },
  { value: 'mentor', label: 'Mentor', description: 'Wise guide who helps the hero on their journey.' },
  { value: 'threshold-guardian', label: 'Threshold Guardian', description: 'Tests the hero before they can enter a new world or stage of their journey.' },
  { value: 'herald', label: 'Herald', description: 'Announces the need for change or the call to adventure.' },
  { value: 'shapeshifter', label: 'Shapeshifter', description: 'Changes form or allegiance, creating doubt and tension.' },
  { value: 'trickster', label: 'Trickster', description: 'Creates disruption and challenges the status quo, often through humor or mischief.' },
  { value: 'lover', label: 'Lover', description: 'Guided by passion, intimacy, and relationships; fears being alone or unloved.' },
  { value: 'sage', label: 'Sage', description: 'Seeks truth and wisdom, offering insight and analysis more than direct action.' },
  { value: 'magician', label: 'Magician', description: 'Understands hidden forces and seeks transformation, change, or healing.' },
  { value: 'orphan', label: 'Orphan', description: 'Feels abandoned or disadvantaged and longs for belonging and safety.' },
  { value: 'jester', label: 'Jester', description: 'Lives in the moment and uses humor and playfulness to reveal truth and relieve tension.' },
  { value: 'ally', label: 'Ally', description: 'Supports the hero and helps them succeed in their journey.' }
];

// Myers-Briggs personality types with descriptions
const personalityTypes = [
  { value: 'INTJ', label: 'INTJ - The Architect', description: 'Strategic, logical, and innovative. INTJs are driven by their own original ideas and have high standards.' },
  { value: 'INTP', label: 'INTP - The Logician', description: 'Analytical, objective, and theoretical. INTPs enjoy exploring ideas and solving complex problems.' },
  { value: 'ENTJ', label: 'ENTJ - The Commander', description: 'Decisive, strategic, and efficient. ENTJs are natural leaders who value competence and logical thinking.' },
  { value: 'ENTP', label: 'ENTP - The Debater', description: 'Quick-thinking, curious, and versatile. ENTPs enjoy intellectual challenges and thinking outside the box.' },
  { value: 'INFJ', label: 'INFJ - The Advocate', description: 'Idealistic, principled, and insightful. INFJs are driven by their values and want to make a positive impact.' },
  { value: 'INFP', label: 'INFP - The Mediator', description: 'Compassionate, creative, and idealistic. INFPs are guided by their core values and seek to understand people deeply.' },
  { value: 'ENFJ', label: 'ENFJ - The Protagonist', description: 'Charismatic, empathetic, and inspiring. ENFJs are natural leaders who bring out the best in others.' },
  { value: 'ENFP', label: 'ENFP - The Campaigner', description: 'Enthusiastic, creative, and sociable. ENFPs are passionate and enjoy exploring possibilities with others.' },
  { value: 'ISTJ', label: 'ISTJ - The Logistician', description: 'Practical, reliable, and systematic. ISTJs value tradition and take a methodical approach to life.' },
  { value: 'ISFJ', label: 'ISFJ - The Defender', description: 'Dedicated, warm, and meticulous. ISFJs are committed to helping others and maintaining harmony.' },
  { value: 'ESTJ', label: 'ESTJ - The Executive', description: 'Efficient, structured, and practical. ESTJs value tradition and are natural organizers and leaders.' },
  { value: 'ESFJ', label: 'ESFJ - The Consul', description: 'Supportive, sociable, and conscientious. ESFJs are focused on maintaining harmony and meeting others\' needs.' },
  { value: 'ISTP', label: 'ISTP - The Virtuoso', description: 'Practical, adaptable, and analytical. ISTPs are skilled troubleshooters who enjoy hands-on problem-solving.' },
  { value: 'ISFP', label: 'ISFP - The Adventurer', description: 'Artistic, sensitive, and practical. ISFPs live in the moment and value personal freedom.' },
  { value: 'ESTP', label: 'ESTP - The Entrepreneur', description: 'Energetic, pragmatic, and perceptive. ESTPs are action-oriented and enjoy taking risks.' },
  { value: 'ESFP', label: 'ESFP - The Entertainer', description: 'Spontaneous, energetic, enthusiastic, and social. ESFPs love being the center of attention and bringing joy to others.' }
];

const mbtiFacetMap = {
  I: 'Introversion',
  E: 'Extraversion',
  N: 'Intuition',
  S: 'Sensing',
  T: 'Thinking',
  F: 'Feeling',
  P: 'Perceiving',
  J: 'Judging'
};

// Common personality trait prompts
const personalityTraitSuggestions = [
  'curious',
  'methodical',
  'impulsive',
  'empathetic',
  'stoic',
  'idealistic',
  'cautious',
  'witty',
  'brooding',
  'optimistic',
  'skeptical',
  'protective',
  'charitable',
  'blunt'
];

// Internal conflict types with descriptions (richer set)
const internalConflictTypes = [
  { value: 'moral-dilemma', label: 'Moral Dilemma', description: 'Torn between two values or duties with real cost on either side.' },
  { value: 'identity-crisis', label: 'Identity Crisis', description: 'Unsure who they are, who they want to be, or where they belong.' },
  { value: 'temptation', label: 'Temptation', description: 'Pulled toward an easy or selfish choice they know is wrong.' },
  { value: 'guilt', label: 'Guilt / Shame', description: 'Haunted by past actions or failures that shape their present.' },
  { value: 'fear', label: 'Fear / Phobia', description: 'Paralyzed by a fear they must confront to move forward.' },
  { value: 'change-resistance', label: 'Resistance to Change', description: 'Clings to the familiar even when growth is required.' },
  { value: 'loyalty-conflict', label: 'Divided Loyalty', description: 'Stuck between two people/causes they care about.' },
  { value: 'impostor-syndrome', label: 'Impostor Syndrome', description: 'Secretly believes they are a fraud and will be exposed.' },
  { value: 'repressed-anger', label: 'Repressed Anger', description: 'Buries resentment until it warps decisions and relationships.' },
  { value: 'destructive-desire', label: 'Self-Sabotage', description: 'Inner drive to ruin their own progress or happiness.' }
];

// External conflict types with descriptions (richer set)
const externalConflictTypes = [
  { value: 'character-vs-character', label: 'Character vs Character' },
  { value: 'character-vs-society', label: 'Character vs Society' },
  { value: 'character-vs-nature', label: 'Character vs Nature' },
  { value: 'character-vs-technology', label: 'Character vs Technology' },
  { value: 'character-vs-supernatural', label: 'Character vs Supernatural' },
  { value: 'character-vs-system', label: 'Character vs System/Institution' },
  { value: 'character-vs-time', label: 'Character vs Time', description: 'A ticking clock or deadline drives the stakes.' },
  { value: 'character-vs-fate', label: 'Character vs Fate', description: 'Battling prophecy, destiny, or inevitability.' },
  { value: 'character-vs-reputation', label: 'Character vs Reputation', description: 'Public perception blocks their goals or safety.' },
  { value: 'character-vs-resources', label: 'Character vs Scarcity', description: 'Limited resources or environment force hard tradeoffs.' }
];

const goalOptions = [
  'Redeem a past mistake',
  'Protect someone important',
  'Uncover the truth',
  'Rise in power or status',
  'Prove their worth',
  'Escape their past',
  'Build a legacy',
  'Save their community',
  'Find belonging',
  'Avenge a wrong'
];

const motivationOptions = [
  'Justice',
  'Love',
  'Fear of failure',
  'Duty',
  'Curiosity',
  'Redemption',
  'Greed',
  'Freedom',
  'Loyalty',
  'Survival'
];

const arcOptions = [
  'Redemption arc',
  'Coming of age',
  'Rise and fall',
  'Reluctant hero',
  'Found family',
  'Rags to riches',
  'Quest for identity',
  'From cynicism to hope',
  'Vengeance to forgiveness',
  'Self-sacrifice'
];

// Character strengths with descriptions
const characterStrengths = [
  { value: 'adaptable', label: 'Adaptable', description: 'Able to adjust to new conditions and situations with ease.' },
  { value: 'ambitious', label: 'Ambitious', description: 'Having a strong desire and determination to succeed.' },
  { value: 'brave', label: 'Brave', description: 'Ready to face and endure danger or pain; showing courage.' },
  { value: 'charismatic', label: 'Charismatic', description: 'Exercising a compelling charm that inspires devotion in others.' },
  { value: 'compassionate', label: 'Compassionate', description: 'Feeling or showing sympathy and concern for others.' },
  { value: 'confident', label: 'Confident', description: 'Feeling or showing certainty about something.' },
  { value: 'creative', label: 'Creative', description: 'Having good imagination or original ideas.' },
  { value: 'decisive', label: 'Decisive', description: 'Able to make decisions quickly and effectively.' },
  { value: 'determined', label: 'Determined', description: 'Having made a firm decision and being resolved not to change it.' },
  { value: 'disciplined', label: 'Disciplined', description: 'Showing a controlled form of behavior or way of working.' },
  { value: 'empathetic', label: 'Empathetic', description: 'Understands and shares the feelings of others.' },
  { value: 'loyal', label: 'Loyal', description: 'Gives firm and constant support or allegiance.' },
  { value: 'resourceful', label: 'Resourceful', description: 'Finds quick and clever ways to overcome difficulties.' },
  { value: 'resilient', label: 'Resilient', description: 'Recovers quickly from setbacks; mentally tough.' },
  { value: 'strategic', label: 'Strategic', description: 'Plans effectively to achieve long-term goals.' },
  { value: 'observant', label: 'Observant', description: 'Notices details others might miss.' },
  { value: 'optimistic', label: 'Optimistic', description: 'Maintains a hopeful and positive outlook.' },
  { value: 'patient', label: 'Patient', description: 'Able to accept delays or problems without getting annoyed.' },
  { value: 'persistent', label: 'Persistent', description: 'Continues firmly despite obstacles.' },
  { value: 'selfless', label: 'Selfless', description: 'Puts the needs of others before their own.' }
];

// Character weaknesses with descriptions
const characterWeaknesses = [
  { value: 'arrogant', label: 'Arrogant', description: 'Having or revealing an exaggerated sense of one\'s own importance or abilities.' },
  { value: 'cowardly', label: 'Cowardly', description: 'Lacking courage when facing danger or difficulty.' },
  { value: 'dishonest', label: 'Dishonest', description: 'Behaving in an untrustworthy way; prone to lying or cheating.' },
  { value: 'impulsive', label: 'Impulsive', description: 'Acting suddenly without careful thought or planning.' },
  { value: 'indecisive', label: 'Indecisive', description: 'Having difficulty in making decisions or choices.' },
  { value: 'insecure', label: 'Insecure', description: 'Not confident or assured; uncertain and anxious.' },
  { value: 'jealous', label: 'Jealous', description: 'Feeling or showing envy of someone or their achievements and advantages.' },
  { value: 'lazy', label: 'Lazy', description: 'Unwilling to work or use energy; characterized by lack of effort.' },
  { value: 'selfish', label: 'Selfish', description: 'Lacking consideration for others; concerned chiefly with one\'s own personal profit or pleasure.' },
  { value: 'stubborn', label: 'Stubborn', description: 'Having or showing dogged determination not to change one\'s attitude or position.' },
  { value: 'cynical', label: 'Cynical', description: 'Believes people are motivated by self-interest; distrustful.' },
  { value: 'reckless', label: 'Reckless', description: 'Acts without thinking about consequences or danger.' },
  { value: 'obsessive', label: 'Obsessive', description: 'Fixates on ideas or tasks to an unhealthy degree.' },
  { value: 'perfectionist', label: 'Perfectionist', description: 'Sets unrealistically high standards and struggles to accept flaws.' },
  { value: 'prideful', label: 'Prideful', description: 'Has excessive self-esteem that can cloud judgment.' },
  { value: 'resentful', label: 'Resentful', description: 'Holds onto anger or feelings of unfair treatment.' },
  { value: 'sarcastic', label: 'Sarcastic', description: 'Relies on biting or mocking remarks that can alienate others.' },
  { value: 'short-tempered', label: 'Short-Tempered', description: 'Angers quickly and finds it hard to stay calm.' },
  { value: 'suspicious', label: 'Suspicious', description: 'Frequently doubts others’ motives or honesty.' },
  { value: 'workaholic', label: 'Workaholic', description: 'Overworks to the detriment of health and relationships.' }
];

// Character voice/speech characteristics
const voiceCharacteristics = [
  { value: 'accent', label: 'Accent', description: 'A distinctive way of pronouncing words, typically associated with a particular country, area, or social class.' },
  { value: 'cadence', label: 'Cadence', description: 'The modulation or inflection of the voice, especially the rise and fall in pitch.' },
  { value: 'clarity', label: 'Clarity', description: 'The quality of being clear and easy to understand.' },
  { value: 'diction', label: 'Diction', description: 'The choice and use of words and phrases in speech or writing.' },
  { value: 'eloquence', label: 'Eloquence', description: 'Fluent or persuasive speaking or writing.' },
  { value: 'hesitation', label: 'Hesitation', description: 'Pausing before speaking, often due to uncertainty or nervousness.' },
  { value: 'inflection', label: 'Inflection', description: 'Changes in the pitch and tone of the voice.' },
  { value: 'pace', label: 'Pace', description: 'The speed at which someone speaks.' },
  { value: 'pitch', label: 'Pitch', description: 'The quality of a sound governed by the rate of vibrations producing it; the degree of highness or lowness of a tone.' },
  { value: 'repetition', label: 'Repetition', description: 'The action of repeating words or phrases.' },
  { value: 'stutter', label: 'Stutter', description: 'A speech disorder characterized by involuntary repetition of sounds or syllables.' },
  { value: 'tone', label: 'Tone', description: 'The quality of a person\'s voice expressing a feeling or emotion.' },
  { value: 'vocabulary', label: 'Vocabulary', description: 'The body of words used in a particular language or subject.' },
  { value: 'volume', label: 'Volume', description: 'The degree of loudness or the intensity of a sound.' }
];

// Age ranges for dropdown
const ageRanges = [
  { value: 'child', label: 'Child (0-12)' },
  { value: 'teenager', label: 'Teenager (13-19)' },
  { value: 'youngAdult', label: 'Young Adult (20-29)' },
  { value: 'adult', label: 'Adult (30-45)' },
  { value: 'middleAged', label: 'Middle-Aged (46-59)' },
  { value: 'senior', label: 'Senior (60+)' },
  { value: 'custom', label: 'Custom Age...' }
];

// Common occupations for dropdown
const commonOccupations = [
  { value: 'student', label: 'Student' },
  { value: 'teacher', label: 'Teacher/Professor' },
  { value: 'doctor', label: 'Doctor/Medical Professional' },
  { value: 'lawyer', label: 'Lawyer/Attorney' },
  { value: 'engineer', label: 'Engineer' },
  { value: 'artist', label: 'Artist/Creative Professional' },
  { value: 'writer', label: 'Writer/Author' },
  { value: 'business', label: 'Business Professional' },
  { value: 'retail', label: 'Retail/Service Worker' },
  { value: 'tech', label: 'Technology Professional' },
  { value: 'nurse', label: 'Nurse/Healthcare Support' },
  { value: 'scientist', label: 'Scientist/Researcher' },
  { value: 'police', label: 'Police/Detective' },
  { value: 'firefighter', label: 'Firefighter/Rescue' },
  { value: 'military', label: 'Military/Defense' },
  { value: 'chef', label: 'Chef/Culinary' },
  { value: 'journalist', label: 'Journalist/Reporter' },
  { value: 'pilot', label: 'Pilot/Aviation' },
  { value: 'farmer', label: 'Farmer/Agriculture' },
  { value: 'construction', label: 'Construction/Trades' },
  { value: 'hospitality', label: 'Hospitality/Events' },
  { value: 'driver', label: 'Driver/Logistics' },
  { value: 'caretaker', label: 'Caretaker/Caregiver' },
  { value: 'athlete', label: 'Athlete/Coach' },
  { value: 'musician', label: 'Musician/Performer' },
  { value: 'unemployed', label: 'Unemployed' },
  { value: 'retired', label: 'Retired' },
  { value: 'custom', label: 'Other (Custom)...' }
];

// Common physical traits
const physicalTraits = {
  bodyTypes: [
    { value: 'athletic', label: 'Athletic' },
    { value: 'slender', label: 'Slender' },
    { value: 'average', label: 'Average' },
    { value: 'muscular', label: 'Muscular' },
    { value: 'heavyset', label: 'Heavyset' },
    { value: 'petite', label: 'Petite' }
  ],
  hairColors: [
    { value: 'black', label: 'Black', color: '#000000' },
    { value: 'brown', label: 'Brown', color: '#8B4513' },
    { value: 'blonde', label: 'Blonde', color: '#F5DEB3' },
    { value: 'red', label: 'Red', color: '#B22222' },
    { value: 'auburn', label: 'Auburn', color: '#A52A2A' },
    { value: 'gray', label: 'Gray/Silver', color: '#808080' },
    { value: 'white', label: 'White', color: '#FFFFFF' },
    { value: 'colorful', label: 'Colorful/Dyed', color: '#FF69B4' }
  ],
  eyeColors: [
    { value: 'brown', label: 'Brown', color: '#8B4513' },
    { value: 'blue', label: 'Blue', color: '#1E90FF' },
    { value: 'green', label: 'Green', color: '#228B22' },
    { value: 'hazel', label: 'Hazel', color: '#B8860B' },
    { value: 'gray', label: 'Gray', color: '#708090' },
    { value: 'amber', label: 'Amber', color: '#FFA500' },
    { value: 'black', label: 'Black', color: '#000000' },
    { value: 'other', label: 'Other', color: '#FF69B4' }
  ],
  skinTones: [
    { value: 'fair', label: 'Fair', color: '#FFE5B4' },
    { value: 'light', label: 'Light', color: '#F5DEB3' },
    { value: 'medium', label: 'Medium', color: '#D2B48C' },
    { value: 'olive', label: 'Olive', color: '#A0522D' },
    { value: 'tan', label: 'Tan', color: '#D2B48C' },
    { value: 'brown', label: 'Brown', color: '#8B4513' },
    { value: 'dark', label: 'Dark', color: '#5C4033' },
    { value: 'ebony', label: 'Ebony', color: '#3D2B1F' }
  ],
  heights: [
    { value: 'veryShort', label: 'Very Short' },
    { value: 'short', label: 'Short' },
    { value: 'average', label: 'Average' },
    { value: 'tall', label: 'Tall' },
    { value: 'veryTall', label: 'Very Tall' }
  ],
  distinguishingFeatures: [
    { value: 'scar', label: 'Scar(s)' },
    { value: 'tattoo', label: 'Tattoo(s)' },
    { value: 'birthmark', label: 'Birthmark' },
    { value: 'freckles', label: 'Freckles' },
    { value: 'piercing', label: 'Piercing(s)' },
    { value: 'glasses', label: 'Glasses/Eyewear' },
    { value: 'beard', label: 'Beard/Facial Hair' },
    { value: 'dimples', label: 'Dimples' },
    { value: 'heterochromia', label: 'Heterochromia (Different colored eyes)' },
    { value: 'prosthetic', label: 'Prosthetic Limb' },
    { value: 'limp', label: 'Limp/Distinctive Gait' },
    { value: 'missing-limb', label: 'Missing Limb' },
    { value: 'burn-scar', label: 'Burn Scars' },
    { value: 'tattoo-sleeve', label: 'Tattoo Sleeve' },
    { value: 'hearing-aid', label: 'Hearing Aid' },
    { value: 'eye-patch', label: 'Eye Patch' },
    { value: 'gold-tooth', label: 'Gold/Distinctive Tooth' },
    { value: 'brace', label: 'Orthopedic Brace' },
    { value: 'wheelchair', label: 'Wheelchair User' }
  ]
};

// Define relationship types with colors
const relationshipTypes = [
  { value: 'friend', label: 'Friend', color: '#4CAF50' },
  { value: 'enemy', label: 'Enemy', color: '#F44336' },
  { value: 'family', label: 'Family', color: '#2196F3' },
  { value: 'lover', label: 'Lover', color: '#E91E63' },
  { value: 'mentor', label: 'Mentor', color: '#9C27B0' },
  { value: 'colleague', label: 'Colleague', color: '#FF9800' },
  { value: 'rival', label: 'Rival', color: '#795548' },
  { value: 'acquaintance', label: 'Acquaintance', color: '#607D8B' }
];

const CharacterSheetModal = ({ open, onClose, character, onSave, allCharacters, projectId }) => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [newCharacter, setNewCharacter] = useState({
    name: '',
    age: '',
    occupation: '',
    role: '',
    archetype: '',
    physicalDescription: '',
    personality: '',
    personalityType: '',
    strengths: [],
    weaknesses: [],
    voiceCharacteristics: [],
    imageUrl: '',
    moodBoardImages: [],
    appearanceDetails: {
      bodyType: '',
      hairColor: '',
      eyeColor: '',
      skinTone: '',
      height: '',
      distinguishingFeatures: []
    }
  });
  const [showRoleDescription, setShowRoleDescription] = useState(false);
  const [showArchetypeDescription, setShowArchetypeDescription] = useState(false);
  const [showPersonalityDescription, setShowPersonalityDescription] = useState(false);
  const [showInternalConflictDescription, setShowInternalConflictDescription] = useState(false);
  const [showExternalConflictDescription, setShowExternalConflictDescription] = useState(false);
  const [newStrength, setNewStrength] = useState('');
  const [newWeakness, setNewWeakness] = useState('');
  const [newVoiceCharacteristic, setNewVoiceCharacteristic] = useState('');
  const [newDistinguishingFeature, setNewDistinguishingFeature] = useState('');
  const [selectedTraitSuggestion, setSelectedTraitSuggestion] = useState('');
  const [moodBoardImages, setMoodBoardImages] = useState([]);
  const [customAge, setCustomAge] = useState('');
  const [customOccupation, setCustomOccupation] = useState('');
  const [customRole, setCustomRole] = useState('');
  const [customArchetype, setCustomArchetype] = useState('');
  const [isGeneratingAppearance, setIsGeneratingAppearance] = useState(false);
  const [isGeneratingPersonality, setIsGeneratingPersonality] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef(null);
  const moodBoardInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Build API endpoint with support for hosted backend
  const getApiEndpoint = (path) => {
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL;
    const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost';
    const isMisconfiguredLocalhostBase =
      !isLocalhost &&
      !!apiBaseUrl &&
      (apiBaseUrl.includes('localhost') || apiBaseUrl.includes('127.0.0.1'));

    const base = (!apiBaseUrl || isMisconfiguredLocalhostBase)
      ? '' // same-origin (works in local dev when proxied)
      : apiBaseUrl.replace(/\/$/, '');

    return `${base}${path}`;
  };

  const cardBaseSx = {
    p: 2,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fff',
    boxShadow: '0 10px 30px rgba(0,0,0,0.06)',
    minHeight: 240,
    display: 'flex',
    flexDirection: 'column'
  };

  const handleGeneratePersonality = async () => {
    setIsGeneratingPersonality(true);
    try {
      const endpoint = getApiEndpoint('/api/ai/generate-personality');
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          name: newCharacter.name,
          gender: newCharacter.gender,
          age: newCharacter.age,
          role: newCharacter.role,
          archetype: newCharacter.archetype,
          personalityType: newCharacter.personalityType,
          personalityText: newCharacter.personality,
          strengths: newCharacter.strengths || [],
          weaknesses: newCharacter.weaknesses || [],
          appearanceDetails: newCharacter.appearanceDetails || {}
        })
      });

      if (!res.ok) {
        throw new Error('AI request failed');
      }

      const data = await res.json();
      if (data?.description) {
        setNewCharacter(prev => ({ ...prev, personality: data.description }));
      }
    } catch (error) {
      console.error('Error generating personality with AI:', error);
    } finally {
      setIsGeneratingPersonality(false);
    }
  };

  const [isGeneratingBackground, setIsGeneratingBackground] = useState(false);
  const backstoryAngleOptions = [
    'Found family',
    'Hidden guilt',
    'Exiled or displaced',
    'Former prodigy',
    'War survivor',
    'Runaway past',
    'Secret identity',
    'Debt or obligation',
    'Betrayed by ally',
    'Family legacy',
    'Reinventing self',
    'Quiet rebellion'
  ];
  const [backstoryTags, setBackstoryTags] = useState([]);
  const splitList = (text = '') => text.split(';').map(t => t.trim()).filter(Boolean);

  const handleGenerateBackground = async () => {
    setIsGeneratingBackground(true);
    try {
      const endpoint = getApiEndpoint('/api/ai/generate-background');
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          name: newCharacter.name,
          gender: newCharacter.gender,
          age: newCharacter.age,
          role: newCharacter.role,
          archetype: newCharacter.archetype,
          personalityType: newCharacter.personalityType,
          strengths: newCharacter.strengths || [],
          weaknesses: newCharacter.weaknesses || [],
          goals: newCharacter.goals,
          motivation: newCharacter.motivation,
          setting: newCharacter.setting || '',
          backstoryTags
        })
      });

      if (!res.ok) {
        throw new Error('AI request failed');
      }

      const data = await res.json();
      if (data?.description) {
        setNewCharacter(prev => ({ ...prev, background: data.description }));
      }
    } catch (error) {
      console.error('Error generating background with AI:', error);
    } finally {
      setIsGeneratingBackground(false);
    }
  };

  const handleSave = () => {
    if (!newCharacter.name) return;
    onSave(newCharacter);
  };

  // Reset form when character changes
  useEffect(() => {
    if (character) {
      // Ensure character has all required properties with defaults
      const updatedCharacter = {
        ...character,
        personality: character.personality || '',
        strengths: character.strengths || [],
        weaknesses: character.weaknesses || [],
        voiceCharacteristics: character.voiceCharacteristics || [],
        moodBoardImages: character.moodBoardImages || [],
        appearanceDetails: character.appearanceDetails || {
          bodyType: '',
          hairColor: '',
          eyeColor: '',
          skinTone: '',
          height: '',
          distinguishingFeatures: []
        }
      };
      
      setNewCharacter(updatedCharacter);
      setShowRoleDescription(!!character.role);
      setShowArchetypeDescription(!!character.archetype);
      setShowPersonalityDescription(!!character.personalityType);
      setShowInternalConflictDescription(!!character.internalConflictType);
      setShowExternalConflictDescription(!!character.externalConflictType);
      
      // Set custom age and occupation if they exist
      if (character.age && !ageRanges.find(a => a.value === character.age)) {
        setCustomAge(character.age);
      }
      
      if (character.occupation && !commonOccupations.find(o => o.value === character.occupation)) {
        setCustomOccupation(character.occupation);
      }

      if (character.role && !characterRoles.find(r => r.value === character.role)) {
        setCustomRole(character.role);
      } else {
        setCustomRole('');
      }

      if (character.archetype && !characterArchetypes.find(a => a.value === character.archetype)) {
        setCustomArchetype(character.archetype);
      } else {
        setCustomArchetype('');
      }
    } else {
      setNewCharacter({
        name: '',
        age: '',
        occupation: '',
        role: '',
        archetype: '',
        physicalDescription: '',
        personality: '',
        personalityType: '',
        strengths: [],
        weaknesses: [],
        voiceCharacteristics: [],
        imageUrl: '',
        moodBoardImages: [],
        appearanceDetails: {
          bodyType: '',
          hairColor: '',
          eyeColor: '',
          skinTone: '',
          height: '',
          distinguishingFeatures: []
        }
      });
      setShowRoleDescription(false);
      setShowArchetypeDescription(false);
      setShowPersonalityDescription(false);
      setShowInternalConflictDescription(false);
      setShowExternalConflictDescription(false);
      setCustomAge('');
      setCustomOccupation('');
      setCustomRole('');
      setCustomArchetype('');
    }
    setActiveTab(0);
    setNewDistinguishingFeature('');
    setSelectedTraitSuggestion('');
  }, [character, open]);

  // Add keyboard navigation for tabs
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      
      // Left arrow key navigates to previous tab
      if (e.key === 'ArrowLeft' && e.altKey) {
        setActiveTab(prev => (prev > 0 ? prev - 1 : 0));
      }
      // Right arrow key navigates to next tab
      else if (e.key === 'ArrowRight' && e.altKey) {
        setActiveTab(prev => (prev < 3 ? prev + 1 : 3));
      }
      // Escape key closes the modal
      else if (e.key === 'Escape') {
        onClose();
      }
      // Enter key with Ctrl saves the character
      else if (e.key === 'Enter' && e.ctrlKey) {
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, handleSave]);

  // Function to handle mood board image uploads
  const handleMoodBoardUpload = async (event) => {
    const files = Array.from(event.target.files);
    for (const file of files) {
      try {
        const url = await uploadCharacterImage(projectId, file);
        setNewCharacter(prev => ({
          ...prev,
          moodBoardImages: [...(prev.moodBoardImages || []), url]
        }));
      } catch (error) {
        console.error('Moodboard upload failed:', error);
      }
    }
  };

  // Function to handle character image upload
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !projectId) return;
    try {
      const url = await uploadCharacterImage(projectId, file);
      setNewCharacter(prev => ({ ...prev, imageUrl: url }));
    } catch (error) {
      console.error('Portrait upload failed:', error);
    }
  };

  // Function to remove mood board image
  const removeMoodBoardImage = (index) => {
    const updatedImages = [...(newCharacter.moodBoardImages || [])];
    updatedImages.splice(index, 1);
    setNewCharacter({...newCharacter, moodBoardImages: updatedImages});
  };

  // Handle drag and drop for moodboard
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);

    const files = Array.from(e.dataTransfer.files).filter(file =>
      file.type.startsWith('image/')
    );

    for (const file of files) {
      try {
        const url = await uploadCharacterImage(projectId, file);
        setNewCharacter(prev => ({
          ...prev,
          moodBoardImages: [...(prev.moodBoardImages || []), url]
        }));
      } catch (error) {
        console.error('Moodboard drop upload failed:', error);
      }
    }
  };

  // Handle URL import for moodboard
  const handleUrlImport = () => {
    if (!imageUrlInput.trim()) return;
    
    // Basic URL validation
    try {
      new URL(imageUrlInput);
      setNewCharacter(prev => ({
        ...prev,
        moodBoardImages: [...(prev.moodBoardImages || []), imageUrlInput.trim()]
      }));
      setImageUrlInput('');
      setShowUrlInput(false);
    } catch {
      // Invalid URL - could show error but for now just ignore
    }
  };

  // Open lightbox
  const openLightbox = (image) => {
    setLightboxImage(image);
  };

  // Close lightbox
  const closeLightbox = () => {
    setLightboxImage(null);
  };

  const getRoleDescription = (roleValue) => {
    const role = characterRoles.find(r => r.value === roleValue);
    return role ? role.description : '';
  };

  const getArchetypeDescription = (archetypeValue) => {
    const archetype = characterArchetypes.find(a => a.value === archetypeValue);
    return archetype ? archetype.description : '';
  };

  const getPersonalityDescription = (typeValue) => {
    const type = personalityTypes.find(t => t.value === typeValue);
    return type ? type.description : '';
  };

  const getInternalConflictDescription = (conflictValue) => {
    const conflict = internalConflictTypes.find(c => c.value === conflictValue);
    return conflict ? conflict.description : '';
  };

  const getExternalConflictDescription = (conflictValue) => {
    const conflict = externalConflictTypes.find(c => c.value === conflictValue);
    return conflict ? conflict.description : '';
  };

  const getStrengthDescription = (strengthValue) => {
    const strength = characterStrengths.find(s => s.value === strengthValue);
    return strength ? strength.description : '';
  };

  const getWeaknessDescription = (weaknessValue) => {
    const weakness = characterWeaknesses.find(w => w.value === weaknessValue);
    return weakness ? weakness.description : '';
  };

  const getVoiceCharacteristicDescription = (voiceCharacteristicValue) => {
    const voiceCharacteristic = voiceCharacteristics.find(v => v.value === voiceCharacteristicValue);
    return voiceCharacteristic ? voiceCharacteristic.description : '';
  };

  const getMbtiFacets = (typeValue) => {
    const padded = (typeValue || '').padEnd(4, '-');
    return {
      energy: padded[0],
      info: padded[1],
      decision: padded[2],
      lifestyle: padded[3]
    };
  };

  const handleMbtiFacetChange = (index, letter) => {
    if (!letter) return;
    const padded = (newCharacter.personalityType || '').padEnd(4, '-').split('');
    padded[index] = letter;
    const updated = padded.join('');
    setNewCharacter(prev => ({ ...prev, personalityType: updated }));
    setShowPersonalityDescription(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewCharacter(prev => ({ ...prev, [name]: value }));
  };

  const formatMbtiSummary = (typeValue) => {
    const facets = getMbtiFacets(typeValue);
    const labels = [
      mbtiFacetMap[facets.energy],
      mbtiFacetMap[facets.info],
      mbtiFacetMap[facets.decision],
      mbtiFacetMap[facets.lifestyle]
    ].filter(Boolean);
    return labels.join(' • ');
  };

  const handleAgeChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setNewCharacter({
        ...newCharacter,
        age: customAge || ''
      });
    } else {
      setNewCharacter({
        ...newCharacter,
        age: value
      });
      setCustomAge('');
    }
  };

  const handleOccupationChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setNewCharacter({
        ...newCharacter,
        occupation: 'custom'
      });
    } else {
      setNewCharacter({
        ...newCharacter,
        occupation: value
      });
      setCustomOccupation('');
    }
  };

  const handleCustomOccupationChange = (e) => {
    const value = e.target.value;
    setCustomOccupation(value);
    setNewCharacter({
      ...newCharacter,
      occupation: value
    });
  };

  const handleRoleChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setNewCharacter({
        ...newCharacter,
        role: 'custom'
      });
    } else {
      setNewCharacter({
        ...newCharacter,
        role: value
      });
      setCustomRole('');
    }
  };

  const handleCustomRoleChange = (e) => {
    const value = e.target.value;
    setCustomRole(value);
    setNewCharacter({
      ...newCharacter,
      role: value
    });
  };

  const handleArchetypeChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setNewCharacter({
        ...newCharacter,
        archetype: 'custom'
      });
    } else {
      setNewCharacter({
        ...newCharacter,
        archetype: value
      });
      setCustomArchetype('');
    }
  };

  const handleCustomArchetypeChange = (e) => {
    const value = e.target.value;
    setCustomArchetype(value);
    setNewCharacter({
      ...newCharacter,
      archetype: value
    });
  };

  const handleAddTraitSuggestion = (trait) => {
    const value = trait.trim();
    if (!value) return;
    const current = (newCharacter.personality || '').trim();
    const sanitizedCurrent = current.replace(/[.,;]\s*$/, '');
    const updated = sanitizedCurrent
      ? `${sanitizedCurrent}, ${value}`
      : value;
    setNewCharacter(prev => ({ ...prev, personality: updated }));
    setSelectedTraitSuggestion(value);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAppearanceDetailChange = (field, value) => {
    // Ensure appearanceDetails exists before updating
    const currentAppearanceDetails = newCharacter.appearanceDetails || {
      bodyType: '',
      hairColor: '',
      eyeColor: '',
      skinTone: '',
      height: '',
      distinguishingFeatures: []
    };
    
    const updatedDetails = { ...currentAppearanceDetails, [field]: value };
    setNewCharacter(prev => ({ ...prev, appearanceDetails: updatedDetails }));
  };

  const handleDistinguishingFeatureToggle = (feature) => {
    // Ensure appearanceDetails and distinguishingFeatures exist
    const currentAppearanceDetails = newCharacter.appearanceDetails || {
      bodyType: '',
      hairColor: '',
      eyeColor: '',
      skinTone: '',
      height: '',
      distinguishingFeatures: []
    };
    
    const currentFeatures = [...(currentAppearanceDetails.distinguishingFeatures || [])];
    const featureIndex = currentFeatures.indexOf(feature);

    if (featureIndex === -1) {
      // Add feature
      currentFeatures.push(feature);
    } else {
      // Remove feature
      currentFeatures.splice(featureIndex, 1);
    }

    const updatedDetails = { 
      ...currentAppearanceDetails, 
      distinguishingFeatures: currentFeatures 
    };

    setNewCharacter(prev => ({ ...prev, appearanceDetails: updatedDetails }));
  };

  const generateAppearanceDescription = () => {
    const details = newCharacter.appearanceDetails || {
      bodyType: '',
      hairColor: '',
      eyeColor: '',
      skinTone: '',
      height: '',
      distinguishingFeatures: []
    };
    let description = '';

    // Build description based on selected traits
    if (details.height) {
      const heightLabel = physicalTraits.heights.find(h => h.value === details.height)?.label || details.height;
      description += `A ${heightLabel.toLowerCase()} `;
    }

    if (details.bodyType) {
      const bodyTypeLabel = physicalTraits.bodyTypes.find(b => b.value === details.bodyType)?.label || details.bodyType;
      description += `${bodyTypeLabel.toLowerCase()} individual `;
    }

    if (details.skinTone) {
      const skinToneLabel = physicalTraits.skinTones.find(s => s.value === details.skinTone)?.label || details.skinTone;
      description += `with ${skinToneLabel.toLowerCase()} skin. `;
    } else {
      description += '. ';
    }

    if (details.hairColor) {
      const hairColorLabel = physicalTraits.hairColors.find(h => h.value === details.hairColor)?.label || details.hairColor;
      description += `They have ${hairColorLabel.toLowerCase()} hair `;
    }

    if (details.eyeColor) {
      const eyeColorLabel = physicalTraits.eyeColors.find(e => e.value === details.eyeColor)?.label || details.eyeColor;
      description += `and ${eyeColorLabel.toLowerCase()} eyes. `;
    } else if (details.hairColor) {
      description += '. ';
    }

    if (details.distinguishingFeatures && details.distinguishingFeatures.length > 0) {
      const featureLabels = (details.distinguishingFeatures || []).map(f => {
        return physicalTraits.distinguishingFeatures.find(df => df.value === f)?.label.toLowerCase() || f;
      });

      if (featureLabels.length === 1) {
        description += `A notable feature is their ${featureLabels[0]}.`;
      } else if (featureLabels.length > 1) {
        const lastFeature = featureLabels.pop();
        description += `Notable features include ${featureLabels.join(', ')} and ${lastFeature}.`;
      }
    }

    return description;
  };

  const handleGenerateDescription = async () => {
    setIsGeneratingAppearance(true);
    try {
      const endpoint = getApiEndpoint('/api/ai/generate-appearance');
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify({
          name: newCharacter.name,
          gender: newCharacter.gender,
          age: newCharacter.age,
          role: newCharacter.role,
          archetype: newCharacter.archetype,
          personalityType: newCharacter.personalityType,
          appearanceDetails: newCharacter.appearanceDetails || {}
        })
      });

      if (!res.ok) {
        throw new Error('AI request failed');
      }

      // Some responses may return an empty body; guard before parsing JSON
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;

      if (data?.description) {
        setNewCharacter(prev => ({ ...prev, physicalDescription: data.description }));
      } else {
        const fallback = generateAppearanceDescription();
        setNewCharacter(prev => ({ ...prev, physicalDescription: fallback }));
      }
    } catch (error) {
      console.error('Error generating appearance with AI, using fallback:', error);
      const fallback = generateAppearanceDescription();
      setNewCharacter(prev => ({ ...prev, physicalDescription: fallback }));
    } finally {
      setIsGeneratingAppearance(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 10px 32px rgba(0,0,0,0.12)',
          border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`
        }
      }}
    >
      <DialogTitle sx={{ 
        px: 3, 
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
            }}
          >
            <PersonIcon sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              {newCharacter.id ? 'Edit Character' : 'Create New Character'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
              {newCharacter.id ? 'Modify character details' : 'Add a new character to your story'}
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: (theme) => theme.palette.text.secondary }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', height: '70vh' }}>
          <Box sx={{ 
            borderRight: 1, 
            borderColor: 'divider', 
            bgcolor: 'background.paper',
            width: '64px'
          }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange} 
              aria-label="character sheet tabs"
              orientation="vertical"
              sx={{ 
                minWidth: '64px',
                '& .MuiTab-root': {
                  alignItems: 'center',
                  justifyContent: 'center',
                  py: 2,
                  px: 2,
                  minHeight: 'auto',
                  minWidth: '64px'
                },
                '& .Mui-selected': {
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'
                }
              }}
            >
              <Tooltip title="Basic character information" placement="right">
                <Tab icon={<PersonIcon />} />
              </Tooltip>
              <Tooltip title="Character appearance and physical description" placement="right">
                <Tab icon={<FaceIcon />} />
              </Tooltip>
              <Tooltip title="Character personality, strengths, and weaknesses" placement="right">
                <Tab icon={<PsychologyIcon />} />
              </Tooltip>
              <Tooltip title="Character background, goals, and conflicts" placement="right">
                <Tab icon={<HistoryIcon />} />
              </Tooltip>
              <Tooltip title="Character mood board images" placement="right">
                <Tab icon={<CollectionsIcon />} />
              </Tooltip>
            </Tabs>
          </Box>

          <Box sx={{ 
            flexGrow: 1, 
            overflow: 'auto', 
            p: 3,
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'background.default'
          }}>
            {activeTab === 0 && (
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: 'primary.main' }}>
                      Basic Information
                    </Typography>
                  </Grid>
                </Grid>
                
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    {/* Character Image Section */}
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        ...cardBaseSx,
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column'
                      }}
                    >
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>
                        Character Image
                      </Typography>
                      
                      {newCharacter.imageUrl ? (
                        <Box 
                          sx={{ 
                            flex: 1,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderRadius: 1,
                            overflow: 'hidden',
                            position: 'relative',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            mb: 2,
                            '&:hover .image-actions': {
                              opacity: 1
                            }
                          }}
                        >
                          <img 
                            src={newCharacter.imageUrl} 
                            alt="Character" 
                            style={{ 
                              maxWidth: '100%', 
                              maxHeight: '100%',
                              objectFit: 'contain'
                            }}
                          />
                          <Box 
                            className="image-actions"
                            sx={{ 
                              position: 'absolute', 
                              top: 0, 
                              right: 0, 
                              opacity: 0, 
                              transition: 'opacity 0.2s',
                              bgcolor: 'rgba(0,0,0,0.5)',
                              borderRadius: '0 0 0 4px'
                            }}
                          >
                            <IconButton 
                              size="small" 
                              color="error" 
                              onClick={() => setNewCharacter({...newCharacter, imageUrl: ''})}
                              sx={{ color: 'white' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      ) : (
                        <Box 
                          sx={{ 
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            border: '2px dashed #ccc',
                            borderRadius: 1,
                            p: 2,
                            mb: 2,
                            bgcolor: 'rgba(0,0,0,0.02)'
                          }}
                        >
                          <PersonIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 1 }} />
                          <Typography variant="body2" color="text.secondary" align="center">
                            No image selected
                          </Typography>
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          component="label"
                          startIcon={<AddIcon />}
                          fullWidth
                          sx={{ borderRadius: 1 }}
                        >
                          Upload Image
                          <input
                            type="file"
                            hidden
                            onChange={handleImageUpload}
                            accept="image/*"
                          />
                        </Button>
                      </Box>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={12} md={8}>
                    {/* Character Details Section */}
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        ...cardBaseSx,
                        height: '100%'
                      }}
                    >
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>
                        Character Details
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            name="name"
                            label="Name"
                            fullWidth
                            value={newCharacter.name}
                            onChange={handleChange}
                            required
                            variant="outlined"
                            placeholder="Enter character's full name"
                            helperText={!newCharacter.name ? "Name is required" : ""}
                            error={!newCharacter.name}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PersonIcon fontSize="small" />
                                </InputAdornment>
                              ),
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            freeSolo
                            options={ageRanges.map(a => a.label)}
                            value={
                              ageRanges.find(a => a.value === newCharacter.age)?.label ||
                              (!ageRanges.find(a => a.value === newCharacter.age) && newCharacter.age ? customAge || newCharacter.age : '')
                            }
                            onChange={(_, val) => {
                              const match = ageRanges.find(a => a.label === val);
                              if (match) {
                                setCustomAge('');
                                setNewCharacter({ ...newCharacter, age: match.value });
                              } else {
                                const customVal = val || '';
                                setCustomAge(customVal);
                                setNewCharacter({ ...newCharacter, age: customVal });
                              }
                            }}
                            onInputChange={(_, val) => {
                              const match = ageRanges.find(a => a.label === val);
                              if (match) {
                                setCustomAge('');
                                setNewCharacter({ ...newCharacter, age: match.value });
                              } else {
                                setCustomAge(val);
                                setNewCharacter({ ...newCharacter, age: val });
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Age Range"
                                placeholder="Select or type an age"
                                type="text"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                              />
                            )}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            freeSolo
                            options={commonOccupations.map(o => o.label)}
                            value={
                              commonOccupations.find(o => o.value === newCharacter.occupation)?.label
                              || customOccupation
                              || ''
                            }
                            onChange={(_, val) => {
                              const match = commonOccupations.find(o => o.label === val);
                              if (match) {
                                setCustomOccupation('');
                                setNewCharacter({ ...newCharacter, occupation: match.value });
                              } else {
                                const customVal = val || '';
                                setCustomOccupation(customVal);
                                setNewCharacter({ ...newCharacter, occupation: customVal });
                              }
                            }}
                            onInputChange={(_, val) => {
                              const match = commonOccupations.find(o => o.label === val);
                              if (match) {
                                setCustomOccupation('');
                                setNewCharacter({ ...newCharacter, occupation: match.value });
                              } else {
                                setCustomOccupation(val);
                                setNewCharacter({ ...newCharacter, occupation: val });
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Occupation"
                                placeholder="Select or type an occupation"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                              />
                            )}
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <TextField
                            name="nickname"
                            label="Nickname/Alias (Optional)"
                            fullWidth
                            value={newCharacter.nickname || ''}
                            onChange={handleChange}
                            variant="outlined"
                            placeholder="Any alternate names or titles"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
                
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    {/* Character Role and Archetype Section */}
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        ...cardBaseSx
                      }}
                    >
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                        <AutoAwesomeIcon sx={{ mr: 1, fontSize: 20 }} />
                        Story Role & Archetype
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            freeSolo
                            options={characterRoles}
                            groupBy={(option) => typeof option === 'string' ? 'Custom' : option.group || 'Other'}
                            getOptionLabel={(option) => typeof option === 'string' ? option : option?.label || ''}
                            value={
                              characterRoles.find(r => r.value === newCharacter.role)
                              || (customRole ? { label: customRole, value: customRole, group: 'Custom' } : '')
                            }
                            onChange={(_, val) => {
                              const option = typeof val === 'string' ? val : val?.label;
                              const match = characterRoles.find(r => r.label === option);
                              if (match) {
                                setCustomRole('');
                                setNewCharacter({ ...newCharacter, role: match.value });
                                setShowRoleDescription(true);
                              } else {
                                const customVal = option || '';
                                setCustomRole(customVal);
                                setNewCharacter({ ...newCharacter, role: customVal });
                                setShowRoleDescription(false);
                              }
                            }}
                            onInputChange={(_, val) => {
                              const match = characterRoles.find(r => r.label === val);
                              if (match) {
                                setCustomRole('');
                                setNewCharacter({ ...newCharacter, role: match.value });
                                setShowRoleDescription(true);
                              } else {
                                setCustomRole(val);
                                setNewCharacter({ ...newCharacter, role: val });
                                setShowRoleDescription(false);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Character Role"
                                placeholder="Select or type a role"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                              />
                            )}
                            renderOption={(props, option) => {
                              const { key, ...rest } = props;
                              const role = typeof option === 'string'
                                ? { label: option, description: 'Custom role', group: 'Custom' }
                                : option;
                              return (
                                <li key={key} {...rest}>
                                  <Box>
                                    <Typography variant="body2">{role.label}</Typography>
                                    {role?.description && (
                                      <Typography variant="caption" color="text.secondary">
                                        {role.description}
                                      </Typography>
                                    )}
                                  </Box>
                                </li>
                              );
                            }}
                            sx={{ mb: 1 }}
                          />
                          {showRoleDescription && newCharacter.role && characterRoles.find(r => r.value === newCharacter.role) && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                              <Typography variant="caption" color="text.secondary">
                                {getRoleDescription(newCharacter.role)}
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            freeSolo
                            options={characterArchetypes.map(a => a.label)}
                            value={
                              characterArchetypes.find(a => a.value === newCharacter.archetype)?.label
                              || customArchetype
                              || ''
                            }
                            onChange={(_, val) => {
                              const match = characterArchetypes.find(a => a.label === val);
                              if (match) {
                                setCustomArchetype('');
                                setNewCharacter({ ...newCharacter, archetype: match.value });
                                setShowArchetypeDescription(true);
                              } else {
                                const customVal = val || '';
                                setCustomArchetype(customVal);
                                setNewCharacter({ ...newCharacter, archetype: customVal });
                                setShowArchetypeDescription(false);
                              }
                            }}
                            onInputChange={(_, val) => {
                              const match = characterArchetypes.find(a => a.label === val);
                              if (match) {
                                setCustomArchetype('');
                                setNewCharacter({ ...newCharacter, archetype: match.value });
                                setShowArchetypeDescription(true);
                              } else {
                                setCustomArchetype(val);
                                setNewCharacter({ ...newCharacter, archetype: val });
                                setShowArchetypeDescription(false);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Character Archetype"
                                placeholder="Select or type an archetype"
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                              />
                            )}
                            renderOption={(props, option) => {
                              const { key, ...rest } = props;
                              const archetype = characterArchetypes.find(a => a.label === option);
                              return (
                                <li key={key} {...rest}>
                                  <Box>
                                    <Typography variant="body2">{option}</Typography>
                                    {archetype?.description && (
                                      <Typography variant="caption" color="text.secondary">
                                        {archetype.description}
                                      </Typography>
                                    )}
                                  </Box>
                                </li>
                              );
                            }}
                            sx={{ mb: 1 }}
                          />
                          {showArchetypeDescription && newCharacter.archetype && characterArchetypes.find(a => a.value === newCharacter.archetype) && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                              <Typography variant="caption" color="text.secondary">
                                {getArchetypeDescription(newCharacter.archetype)}
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
                
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    {/* Additional Details Section */}
                    <Paper 
                      elevation={0} 
                      sx={{ 
                        ...cardBaseSx
                      }}
                    >
                      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
                        <FaceIcon sx={{ mr: 1, fontSize: 20 }} />
                        Additional Details
                      </Typography>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            name="nationality"
                            label="Nationality/Origin"
                            fullWidth
                            value={newCharacter.nationality || ''}
                            onChange={handleChange}
                            variant="outlined"
                            placeholder="Character's country or place of origin"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                          />
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel id="gender-select-label">Gender</InputLabel>
                            <Select
                              labelId="gender-select-label"
                              name="gender"
                              value={newCharacter.gender || ''}
                              label="Gender"
                              onChange={handleChange}
                              sx={{ borderRadius: 1 }}
                            >
                              <MenuItem value=""><em>None</em></MenuItem>
                              <MenuItem value="male">Male</MenuItem>
                              <MenuItem value="female">Female</MenuItem>
                              <MenuItem value="non-binary">Non-binary</MenuItem>
                              <MenuItem value="other">Other</MenuItem>
                              <MenuItem value="unspecified">Unspecified</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {activeTab === 1 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: 'primary.main' }}>
                    Physical Appearance
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      ...cardBaseSx,
                      mb: 3
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 500 }}>
                      Physical Traits
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="body-type-label">Body Type</InputLabel>
                          <Select
                            labelId="body-type-label"
                            value={newCharacter.appearanceDetails?.bodyType || ''}
                            label="Body Type"
                            onChange={(e) => handleAppearanceDetailChange('bodyType', e.target.value)}
                            sx={{ borderRadius: 1 }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {physicalTraits.bodyTypes.map((type) => (
                              <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="height-label">Height</InputLabel>
                          <Select
                            labelId="height-label"
                            value={newCharacter.appearanceDetails?.height || ''}
                            label="Height"
                            onChange={(e) => handleAppearanceDetailChange('height', e.target.value)}
                            sx={{ borderRadius: 1 }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {physicalTraits.heights.map((height) => (
                              <MenuItem key={height.value} value={height.value}>{height.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="skin-tone-label">Skin Tone</InputLabel>
                          <Select
                            labelId="skin-tone-label"
                            value={newCharacter.appearanceDetails?.skinTone || ''}
                            label="Skin Tone"
                            onChange={(e) => handleAppearanceDetailChange('skinTone', e.target.value)}
                            sx={{ borderRadius: 1 }}
                            renderValue={(selected) => {
                              const selectedTone = physicalTraits.skinTones.find(tone => tone.value === selected);
                              return (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box 
                                    sx={{ 
                                      width: 16, 
                                      height: 16, 
                                      borderRadius: '50%', 
                                      bgcolor: selectedTone?.color || '#fff',
                                      border: '1px solid #ccc',
                                      mr: 1
                                    }} 
                                  />
                                  {selectedTone?.label || selected}
                                </Box>
                              );
                            }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {physicalTraits.skinTones.map((tone) => (
                              <MenuItem key={tone.value} value={tone.value}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box 
                                    sx={{ 
                                      width: 16, 
                                      height: 16, 
                                      borderRadius: '50%', 
                                      bgcolor: tone.color,
                                      border: '1px solid #ccc',
                                      mr: 1
                                    }} 
                                  />
                                  {tone.label}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="hair-color-label">Hair Color</InputLabel>
                          <Select
                            labelId="hair-color-label"
                            value={newCharacter.appearanceDetails?.hairColor || ''}
                            label="Hair Color"
                            onChange={(e) => handleAppearanceDetailChange('hairColor', e.target.value)}
                            sx={{ borderRadius: 1 }}
                            renderValue={(selected) => {
                              const selectedColor = physicalTraits.hairColors.find(color => color.value === selected);
                              return (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box 
                                    sx={{ 
                                      width: 16, 
                                      height: 16, 
                                      borderRadius: '50%', 
                                      bgcolor: selectedColor?.color || '#fff',
                                      border: '1px solid #ccc',
                                      mr: 1
                                    }} 
                                  />
                                  {selectedColor?.label || selected}
                                </Box>
                              );
                            }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {physicalTraits.hairColors.map((color) => (
                              <MenuItem key={color.value} value={color.value}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box 
                                    sx={{ 
                                      width: 16, 
                                      height: 16, 
                                      borderRadius: '50%', 
                                      bgcolor: color.color,
                                      border: '1px solid #ccc',
                                      mr: 1
                                    }} 
                                  />
                                  {color.label}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="eye-color-label">Eye Color</InputLabel>
                          <Select
                            labelId="eye-color-label"
                            value={newCharacter.appearanceDetails?.eyeColor || ''}
                            label="Eye Color"
                            onChange={(e) => handleAppearanceDetailChange('eyeColor', e.target.value)}
                            sx={{ borderRadius: 1 }}
                            renderValue={(selected) => {
                              const selectedColor = physicalTraits.eyeColors.find(color => color.value === selected);
                              return (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box 
                                    sx={{ 
                                      width: 16, 
                                      height: 16, 
                                      borderRadius: '50%', 
                                      bgcolor: selectedColor?.color || '#fff',
                                      border: '1px solid #ccc',
                                      mr: 1
                                    }} 
                                  />
                                  {selectedColor?.label || selected}
                                </Box>
                              );
                            }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {physicalTraits.eyeColors.map((color) => (
                              <MenuItem key={color.value} value={color.value}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Box 
                                    sx={{ 
                                      width: 16, 
                                      height: 16, 
                                      borderRadius: '50%', 
                                      bgcolor: color.color,
                                      border: '1px solid #ccc',
                                      mr: 1
                                    }} 
                                  />
                                  {color.label}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                          Distinguishing Features
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
                          <FormControl sx={{ minWidth: 200 }} size="small">
                            <InputLabel id="distinguishing-feature-select-label">Add Feature</InputLabel>
                            <Select
                              labelId="distinguishing-feature-select-label"
                              value={newDistinguishingFeature}
                              label="Add Feature"
                              onChange={(e) => setNewDistinguishingFeature(e.target.value)}
                            >
                              {physicalTraits.distinguishingFeatures.map((feature) => (
                                <MenuItem key={feature.value} value={feature.value}>{feature.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            label="Custom Feature"
                            value={newDistinguishingFeature}
                            onChange={(e) => setNewDistinguishingFeature(e.target.value)}
                            placeholder="e.g., mismatched eyes, nasal scar"
                            sx={{ minWidth: 240, flexGrow: 1 }}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            disabled={!newDistinguishingFeature.trim()}
                            onClick={() => {
                              const value = newDistinguishingFeature.trim();
                              const currentFeatures = newCharacter.appearanceDetails?.distinguishingFeatures || [];
                              if (value && !currentFeatures.includes(value)) {
                                handleAppearanceDetailChange('distinguishingFeatures', [...currentFeatures, value]);
                              }
                              setNewDistinguishingFeature('');
                            }}
                          >
                            Add
                          </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(newCharacter.appearanceDetails?.distinguishingFeatures || []).map((feature, index) => (
                            <Chip
                              key={`${feature}-${index}`}
                              label={physicalTraits.distinguishingFeatures.find(df => df.value === feature)?.label || feature}
                              color="primary"
                              variant="outlined"
                              onDelete={() => {
                                const currentFeatures = newCharacter.appearanceDetails?.distinguishingFeatures || [];
                                const updated = currentFeatures.filter((_, i) => i !== index);
                                handleAppearanceDetailChange('distinguishingFeatures', updated);
                              }}
                            />
                          ))}
                        </Box>
                      </Grid>
                    </Grid>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button 
                        size="small"
                        variant="outlined" 
                        onClick={handleGenerateDescription}
                        startIcon={isGeneratingAppearance ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                        sx={{ borderRadius: 1 }}
                        disabled={isGeneratingAppearance}
                      >
                        {isGeneratingAppearance ? 'Generating...' : 'AI Generate'}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    name="physicalDescription"
                    label="Physical Description"
                    fullWidth
                    multiline
                    rows={6}
                    value={newCharacter.physicalDescription}
                    onChange={handleChange}
                    placeholder="Describe your character's physical appearance..."
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                  />
                </Grid>
              </Grid>
            )}

            {activeTab === 2 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: 'primary.main' }}>
                    Personality Traits
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ ...cardBaseSx, mb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth sx={{ mb: 1.5 }}>
                          <InputLabel id="personality-type-label">Personality Type</InputLabel>
                          <Select
                            labelId="personality-type-label"
                            name="personalityType"
                            value={newCharacter.personalityType}
                            label="Personality Type"
                            onChange={(e) => {
                              handleChange(e);
                              setShowPersonalityDescription(!!e.target.value);
                            }}
                            sx={{ borderRadius: 1 }}
                          >
                            {personalityTypes.map((type) => (
                              <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.25,
                            borderRadius: 2,
                            borderColor: 'divider',
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'
                          }}
                        >
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Build your type
                          </Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 1 }}>
                            <ToggleButtonGroup
                              size="small"
                              value={getMbtiFacets(newCharacter.personalityType).energy}
                              exclusive
                              onChange={(_, val) => handleMbtiFacetChange(0, val)}
                              color="primary"
                              fullWidth
                            >
                              <ToggleButton value="I">Introvert (I)</ToggleButton>
                              <ToggleButton value="E">Extrovert (E)</ToggleButton>
                            </ToggleButtonGroup>
                            <ToggleButtonGroup
                              size="small"
                              value={getMbtiFacets(newCharacter.personalityType).info}
                              exclusive
                              onChange={(_, val) => handleMbtiFacetChange(1, val)}
                              color="primary"
                              fullWidth
                            >
                              <ToggleButton value="N">Intuition (N)</ToggleButton>
                              <ToggleButton value="S">Sensing (S)</ToggleButton>
                            </ToggleButtonGroup>
                            <ToggleButtonGroup
                              size="small"
                              value={getMbtiFacets(newCharacter.personalityType).decision}
                              exclusive
                              onChange={(_, val) => handleMbtiFacetChange(2, val)}
                              color="primary"
                              fullWidth
                            >
                              <ToggleButton value="T">Thinking (T)</ToggleButton>
                              <ToggleButton value="F">Feeling (F)</ToggleButton>
                            </ToggleButtonGroup>
                            <ToggleButtonGroup
                              size="small"
                              value={getMbtiFacets(newCharacter.personalityType).lifestyle}
                              exclusive
                              onChange={(_, val) => handleMbtiFacetChange(3, val)}
                              color="primary"
                              fullWidth
                            >
                              <ToggleButton value="P">Perceiving (P)</ToggleButton>
                              <ToggleButton value="J">Judging (J)</ToggleButton>
                            </ToggleButtonGroup>
                          </Box>
                          {newCharacter.personalityType && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 600 }}>
                              {newCharacter.personalityType.toUpperCase()} — {formatMbtiSummary(newCharacter.personalityType)}
                            </Typography>
                          )}
                        </Paper>
                        {showPersonalityDescription && newCharacter.personalityType && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                            <Typography variant="caption" color="text.secondary">
                              {getPersonalityDescription(newCharacter.personalityType)}
                            </Typography>
                          </Box>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          name="personality"
                          label="Personality"
                          fullWidth
                          multiline
                          rows={6}
                          value={newCharacter.personality}
                          onChange={handleChange}
                          placeholder="Describe your character's personality traits, habits, and behaviors..."
                          InputLabelProps={{ shrink: true }}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleGeneratePersonality}
                            startIcon={<AutoAwesomeIcon />}
                            disabled={isGeneratingPersonality}
                            sx={{ borderRadius: 1 }}
                          >
                            {isGeneratingPersonality ? 'Generating…' : 'AI Generate Personality'}
                          </Button>
                        </Box>
                        <Paper variant="outlined" sx={{ mt: 1, p: 1, borderRadius: 1.5, borderColor: 'divider', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Quick trait picks (click to append)
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {personalityTraitSuggestions.map((trait) => {
                              const selected = selectedTraitSuggestion === trait;
                              return (
                                <Chip
                                  key={trait}
                                  label={trait}
                                  clickable
                                  size="small"
                                  variant="outlined"
                                  color={selected ? 'primary' : 'default'}
                                  onClick={() => handleAddTraitSuggestion(trait)}
                                  sx={{
                                    borderColor: selected ? 'primary.main' : 'divider',
                                    bgcolor: selected ? 'primary.light' : 'transparent',
                                    color: selected ? 'primary.contrastText' : 'text.primary'
                                  }}
                                />
                              );
                            })}
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2} direction="column">
                    <Grid item xs={12}>
                      <Paper sx={{ ...cardBaseSx }}>
                        <Typography variant="subtitle1" gutterBottom>Character Strengths</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <FormControl sx={{ minWidth: 200, mr: 2 }}>
                            <InputLabel id="strength-select-label">Add Strength</InputLabel>
                            <Select
                              labelId="strength-select-label"
                              value={newStrength}
                              label="Add Strength"
                              onChange={(e) => setNewStrength(e.target.value)}
                              size="small"
                            >
                              {characterStrengths.map((strength) => (
                                <MenuItem key={strength.value} value={strength.value}>{strength.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            label="Custom Strength"
                            variant="outlined"
                            size="small"
                            sx={{ mr: 2, flexGrow: 1 }}
                            value={newStrength}
                            onChange={(e) => setNewStrength(e.target.value)}
                            placeholder="Enter a custom strength"
                          />
                          <Button 
                            variant="contained" 
                            color="primary"
                            size="small"
                            onClick={() => {
                              if (newStrength && !newCharacter.strengths.includes(newStrength)) {
                                setNewCharacter({
                                  ...newCharacter,
                                  strengths: [...newCharacter.strengths, newStrength]
                                });
                                setNewStrength('');
                              }
                            }}
                            disabled={!newStrength}
                          >
                            Add
                          </Button>
                        </Box>
                        {newStrength && (
                          <Box sx={{ mb: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                            <Typography variant="caption" color="text.secondary">
                              {getStrengthDescription(newStrength) || 'Custom strength'}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(newCharacter.strengths || []).map((strength, index) => (
                            <Chip
                              key={index}
                              label={characterStrengths.find(s => s.value === strength)?.label || strength}
                              color="success"
                              variant="outlined"
                              onDelete={() => {
                                setNewCharacter({
                                  ...newCharacter,
                                  strengths: newCharacter.strengths.filter((_, i) => i !== index)
                                });
                              }}
                            />
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper sx={{ ...cardBaseSx }}>
                        <Typography variant="subtitle1" gutterBottom>Character Weaknesses</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <FormControl sx={{ minWidth: 200, mr: 2 }}>
                            <InputLabel id="weakness-select-label">Add Weakness</InputLabel>
                            <Select
                              labelId="weakness-select-label"
                              value={newWeakness}
                              label="Add Weakness"
                              onChange={(e) => setNewWeakness(e.target.value)}
                              size="small"
                            >
                              {characterWeaknesses.map((weakness) => (
                                <MenuItem key={weakness.value} value={weakness.value}>{weakness.label}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            label="Custom Weakness"
                            variant="outlined"
                            size="small"
                            sx={{ mr: 2, flexGrow: 1 }}
                            value={newWeakness}
                            onChange={(e) => setNewWeakness(e.target.value)}
                            placeholder="Enter a custom weakness"
                          />
                          <Button 
                            variant="contained" 
                            color="primary"
                            size="small"
                            onClick={() => {
                              if (newWeakness && !newCharacter.weaknesses.includes(newWeakness)) {
                                setNewCharacter({
                                  ...newCharacter,
                                  weaknesses: [...newCharacter.weaknesses, newWeakness]
                                });
                                setNewWeakness('');
                              }
                            }}
                            disabled={!newWeakness}
                          >
                            Add
                          </Button>
                        </Box>
                        {newWeakness && (
                          <Box sx={{ mb: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                            <Typography variant="caption" color="text.secondary">
                              {getWeaknessDescription(newWeakness) || 'Custom weakness'}
                            </Typography>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(newCharacter.weaknesses || []).map((weakness, index) => (
                            <Chip
                              key={index}
                              label={characterWeaknesses.find(w => w.value === weakness)?.label || weakness}
                              color="error"
                              variant="outlined"
                              onDelete={() => {
                                setNewCharacter({
                                  ...newCharacter,
                                  weaknesses: newCharacter.weaknesses.filter((_, i) => i !== index)
                                });
                              }}
                            />
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ ...cardBaseSx }}>
                    <Typography variant="subtitle1" gutterBottom>Voice Characteristics</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <FormControl sx={{ minWidth: 200, mr: 2 }}>
                        <InputLabel id="voice-characteristic-select-label">Add Voice Characteristic</InputLabel>
                        <Select
                          labelId="voice-characteristic-select-label"
                          value={newVoiceCharacteristic}
                          label="Add Voice Characteristic"
                          onChange={(e) => setNewVoiceCharacteristic(e.target.value)}
                          size="small"
                        >
                          {voiceCharacteristics.map((voiceCharacteristic) => (
                            <MenuItem key={voiceCharacteristic.value} value={voiceCharacteristic.value}>{voiceCharacteristic.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label="Custom Voice Characteristic"
                        variant="outlined"
                        size="small"
                        sx={{ mr: 2, flexGrow: 1 }}
                        value={newVoiceCharacteristic}
                        onChange={(e) => setNewVoiceCharacteristic(e.target.value)}
                        placeholder="Enter a custom voice characteristic"
                      />
                      <Button 
                        variant="contained" 
                        color="primary"
                        size="small"
                        onClick={() => {
                          if (newVoiceCharacteristic && !newCharacter.voiceCharacteristics.includes(newVoiceCharacteristic)) {
                            setNewCharacter({
                              ...newCharacter,
                              voiceCharacteristics: [...newCharacter.voiceCharacteristics, newVoiceCharacteristic]
                            });
                            setNewVoiceCharacteristic('');
                          }
                        }}
                        disabled={!newVoiceCharacteristic}
                      >
                        Add
                      </Button>
                    </Box>
                    {newVoiceCharacteristic && (
                      <Box sx={{ mb: 2, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                        <Typography variant="caption" color="text.secondary">
                          {getVoiceCharacteristicDescription(newVoiceCharacteristic) || 'Custom voice characteristic'}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(newCharacter.voiceCharacteristics || []).map((voiceCharacteristic, index) => (
                        <Chip
                          key={index}
                          label={voiceCharacteristics.find(v => v.value === voiceCharacteristic)?.label || voiceCharacteristic}
                          color="info"
                          variant="outlined"
                          onDelete={() => {
                            setNewCharacter({
                              ...newCharacter,
                              voiceCharacteristics: newCharacter.voiceCharacteristics.filter((_, i) => i !== index)
                            });
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {activeTab === 3 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
                    Background & Conflicts
                  </Typography>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Paper elevation={0} sx={{ ...cardBaseSx, minHeight: 0, gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Background</Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleGenerateBackground}
                        disabled={isGeneratingBackground}
                        startIcon={isGeneratingBackground ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                      >
                        {isGeneratingBackground ? 'Generating...' : 'AI Generate'}
                      </Button>
                    </Box>
                    <TextField
                      name="background"
                      label="Background"
                      fullWidth
                      multiline
                      rows={6}
                      value={newCharacter.background}
                      onChange={handleChange}
                      placeholder="Ground them in a specific past that shapes who they are now."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                    />
                    <Autocomplete
                      multiple
                      options={backstoryAngleOptions}
                      value={backstoryTags}
                      onChange={(_, val) => setBackstoryTags(val || [])}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Guide the AI (optional tags)"
                          placeholder="Add tags like 'found family', 'hidden guilt'..."
                        />
                      )}
                      size="small"
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Paper elevation={0} sx={{ ...cardBaseSx, minHeight: 0, gap: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Goals, Motivation, Arc</Typography>
                    <Autocomplete
                      multiple
                      freeSolo
                      options={goalOptions}
                      value={splitList(newCharacter.goals)}
                      onChange={(_, val) => setNewCharacter({ ...newCharacter, goals: (val || []).join('; ') })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Goals"
                          placeholder="Select or type goals..."
                        />
                      )}
                    />
                    <Autocomplete
                      multiple
                      freeSolo
                      options={motivationOptions}
                      value={splitList(newCharacter.motivation)}
                      onChange={(_, val) => setNewCharacter({ ...newCharacter, motivation: (val || []).join('; ') })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Motivations"
                          placeholder="Select or type motivations..."
                        />
                      )}
                    />
                    <Autocomplete
                      freeSolo
                      options={arcOptions}
                      value={newCharacter.arc || ''}
                      onChange={(_, val) => setNewCharacter({ ...newCharacter, arc: val || '' })}
                      onInputChange={(_, val) => setNewCharacter({ ...newCharacter, arc: val || '' })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Character Arc"
                          placeholder="Select or type an arc..."
                        />
                      )}
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ ...cardBaseSx, minHeight: 0, gap: 1.25 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Internal Conflict</Typography>
                    <FormControl fullWidth>
                      <InputLabel id="internal-conflict-type-label">Conflict Type</InputLabel>
                      <Select
                        labelId="internal-conflict-type-label"
                        name="internalConflictType"
                        value={newCharacter.internalConflictType}
                        label="Conflict Type"
                        onChange={(e) => {
                          handleChange(e);
                          setShowInternalConflictDescription(!!e.target.value);
                        }}
                      >
                        {internalConflictTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {showInternalConflictDescription && newCharacter.internalConflictType && (
                      <Box sx={{ mt: 0.5, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                        <Typography variant="caption" color="text.secondary">
                          {getInternalConflictDescription(newCharacter.internalConflictType)}
                        </Typography>
                      </Box>
                    )}
                    <TextField
                      name="internalConflict"
                      label="Internal Conflict Description"
                      fullWidth
                      multiline
                      rows={3}
                      value={newCharacter.internalConflict}
                      onChange={handleChange}
                      placeholder="Describe the inner struggle that shapes their choices."
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ ...cardBaseSx, minHeight: 0, gap: 1.25 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>External Conflict</Typography>
                    <FormControl fullWidth>
                      <InputLabel id="external-conflict-type-label">Conflict Type</InputLabel>
                      <Select
                        labelId="external-conflict-type-label"
                        name="externalConflictType"
                        value={newCharacter.externalConflictType}
                        label="Conflict Type"
                        onChange={(e) => {
                          handleChange(e);
                          setShowExternalConflictDescription(!!e.target.value);
                        }}
                      >
                        {externalConflictTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {showExternalConflictDescription && newCharacter.externalConflictType && (
                      <Box sx={{ mt: 0.5, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                        <Typography variant="caption" color="text.secondary">
                          {getExternalConflictDescription(newCharacter.externalConflictType)}
                        </Typography>
                      </Box>
                    )}
                    <TextField
                      name="externalConflict"
                      label="External Conflict Description"
                      fullWidth
                      multiline
                      rows={3}
                      value={newCharacter.externalConflict}
                      onChange={handleChange}
                      placeholder="Describe the forces opposing them in the world."
                    />
                  </Paper>
                </Grid>
              </Grid>
            )}

            {activeTab === 4 && (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, color: 'primary.main' }}>
                      Mood Board
                    </Typography>
                    <Chip 
                      label={`${(newCharacter.moodBoardImages || []).length} images`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  {/* Drag & Drop Zone */}
                  <Box
                    ref={dropZoneRef}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                      border: isDraggingOver ? '3px dashed' : '2px dashed',
                      borderColor: isDraggingOver ? 'primary.main' : 'divider',
                      borderRadius: 2,
                      p: 4,
                      mb: 3,
                      textAlign: 'center',
                      bgcolor: isDraggingOver ? (theme) => alpha(theme.palette.primary.main, 0.08) : 'background.paper',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: 'primary.light',
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04)
                      }
                    }}
                    onClick={() => moodBoardInputRef.current?.click()}
                  >
                    <input
                      ref={moodBoardInputRef}
                      type="file"
                      multiple
                      hidden
                      onChange={handleMoodBoardUpload}
                      accept="image/*"
                    />
                    <CloudUploadIcon sx={{ fontSize: 48, color: isDraggingOver ? 'primary.main' : 'text.secondary', mb: 1 }} />
                    <Typography variant="h6" color={isDraggingOver ? 'primary.main' : 'text.secondary'} gutterBottom>
                      {isDraggingOver ? 'Drop images here' : 'Drag & drop images here'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      or click to browse files
                    </Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      component="label"
                      sx={{ borderRadius: 2 }}
                    >
                      Upload Images
                      <input
                        type="file"
                        multiple
                        hidden
                        onChange={handleMoodBoardUpload}
                        accept="image/*"
                      />
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      onClick={() => setShowUrlInput(!showUrlInput)}
                      sx={{ borderRadius: 2 }}
                    >
                      Add from URL
                    </Button>
                  </Box>

                  {/* URL Input */}
                  {showUrlInput && (
                    <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'background.default', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Paste image URL here..."
                          value={imageUrlInput}
                          onChange={(e) => setImageUrlInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleUrlImport()}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <LinkIcon fontSize="small" />
                              </InputAdornment>
                            )
                          }}
                        />
                        <Button 
                          variant="contained" 
                          onClick={handleUrlImport}
                          disabled={!imageUrlInput.trim()}
                          sx={{ borderRadius: 2 }}
                        >
                          Add
                        </Button>
                        <IconButton onClick={() => { setShowUrlInput(false); setImageUrlInput(''); }}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  )}

                  {/* Tips Section */}
                  <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: (theme) => alpha(theme.palette.info.main, 0.08), borderRadius: 2, border: '1px solid', borderColor: (theme) => alpha(theme.palette.info.main, 0.2) }}>
                    <Typography variant="body2" color="text.secondary" component="div">
                      <strong>💡 Tip:</strong> Include images representing your character's style, environment, props, color palette, and emotional states.
                    </Typography>
                  </Paper>

                  {/* Image Grid */}
                  {(newCharacter.moodBoardImages || []).length > 0 ? (
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', 
                      gap: 2,
                      maxHeight: 450,
                      overflowY: 'auto',
                      p: 1
                    }}>
                      {(newCharacter.moodBoardImages || []).map((image, index) => (
                        <Paper
                          key={index}
                          elevation={2}
                          sx={{
                            position: 'relative',
                            aspectRatio: '1',
                            borderRadius: 2,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              transform: 'scale(1.02)',
                              boxShadow: 6
                            },
                            '&:hover .image-overlay': {
                              opacity: 1
                            }
                          }}
                          onClick={() => openLightbox(image)}
                        >
                          <img
                            src={image}
                            alt={`Mood board ${index + 1}`}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          <Box
                            className="image-overlay"
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              bgcolor: 'rgba(0,0,0,0.5)',
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1
                            }}
                          >
                            <Tooltip title="View full size">
                              <IconButton 
                                size="small" 
                                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
                                onClick={(e) => { e.stopPropagation(); openLightbox(image); }}
                              >
                                <ZoomInIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove">
                              <IconButton 
                                size="small" 
                                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', '&:hover': { bgcolor: 'error.main' } }}
                                onClick={(e) => { e.stopPropagation(); removeMoodBoardImage(index); }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Chip
                            label={index + 1}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 8,
                              left: 8,
                              bgcolor: 'rgba(0,0,0,0.6)',
                              color: 'white',
                              fontSize: '0.7rem',
                              height: 20
                            }}
                          />
                        </Paper>
                      ))}
                    </Box>
                  ) : (
                    <Box 
                      sx={{ 
                        width: '100%', 
                        py: 6,
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: 'background.default',
                        borderRadius: 2
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="body1" color="text.secondary" gutterBottom>
                        No mood board images yet
                      </Typography>
                      <Typography variant="body2" color="text.disabled">
                        Drag & drop or upload images to build your character's visual identity
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            )}

            {/* Lightbox Dialog */}
            <Dialog
              open={!!lightboxImage}
              onClose={closeLightbox}
              maxWidth="lg"
              PaperProps={{
                sx: {
                  bgcolor: 'transparent',
                  boxShadow: 'none',
                  overflow: 'visible'
                }
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <IconButton
                  onClick={closeLightbox}
                  sx={{
                    position: 'absolute',
                    top: -40,
                    right: 0,
                    color: 'white',
                    bgcolor: 'rgba(0,0,0,0.5)',
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                  }}
                >
                  <CloseIcon />
                </IconButton>
                {lightboxImage && (
                  <img
                    src={lightboxImage}
                    alt="Mood board preview"
                    style={{
                      maxWidth: '90vw',
                      maxHeight: '85vh',
                      objectFit: 'contain',
                      borderRadius: 8
                    }}
                  />
                )}
              </Box>
            </Dialog>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ 
        px: 3, 
        py: 2, 
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        justifyContent: 'space-between'
      }}>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {newCharacter.id ? 'Editing existing character' : 'Creating new character'}
          </Typography>
        </Box>
        <Box>
          <Button onClick={onClose} sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={!newCharacter.name}
            startIcon={<SaveIcon />}
          >
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CharacterSheetModal;
